// Turbo and Stimulus
import * as Turbo from '@hotwired/turbo'
// Registers turbo-rails' <turbo-cable-stream-source> custom element (+ its ActionCable
// consumer) so `turbo_stream_from` actually subscribes. Bare @hotwired/turbo ships Frames
// and Drive but NOT the cable stream source — without this, turbo_stream_from is inert and
// every server broadcast reaches nobody (which is why copilot live updates needed a refresh).
import '@hotwired/turbo-rails'
import { application } from 'controllers/application'

// Bali ViewComponents
import { registerAll } from 'bali'

// ActiveStorage
import * as ActiveStorage from '@rails/activestorage'

// Trix editor
import 'trix'
import '@rails/actiontext'

// ActionCable
import { createConsumer } from '@rails/actioncable'

// Sentry
import * as Sentry from '@sentry/browser'

// Third-party
import ScrollReveal from '@stimulus-components/scroll-reveal'
import ahoy from 'ahoy.js' // eslint-disable-line
import 'intersection-observer'
import TextareaAutogrow from 'stimulus-textarea-autogrow'

// Turbo Native bridge
import 'src/turbo_native/bridge'

// Meta Pixel PageView on Turbo navigation
import 'src/tracking/meta_pixel'

// Custom app controllers
import NativeBridgeMessageSenderController from 'controllers/native_bridge_message_sender_controller'
import FixedNavbarWithBannerController from 'controllers/fixed_navbar_with_banner_controller'
import FacebookEventsController from 'controllers/facebook_events_controller'
import GoogleAdsController from 'controllers/google_ads_controller'
import SelectedItemController from 'controllers/selected_item_controller'
import StripeCheckoutController from 'controllers/stripe_checkout_controller'
import PasswordVisibilityController from 'controllers/password_visibility_controller'
import BillingToggleController from 'controllers/billing_toggle_controller'

import { DeliveryTimeFieldController } from 'delivery-time-field'
import { FeedbackFormController } from 'feedback-form'
import { SlimSelectWithDisablerController } from 'slim-select-with-disabler'

import { RecipeStepNavigatorController } from 'flaming_os/recipe-step-navigator'
import HomepageController from 'controllers/homepage_controller'
import InvoiceFormController from 'controllers/acct/invoice_form_controller'
import SignatureChallengeController from 'controllers/compliance/signature_challenge_controller'
import DecisionTreeController from 'controllers/compliance/decision_tree_controller'
import CriticalLimitController from 'controllers/compliance/critical_limit_controller'
import CcpKeypadController from 'controllers/compliance/ccp_keypad_controller'
import CorrectiveActionFormController from 'controllers/compliance/corrective_action_form_controller'
import QuickLogController from 'controllers/compliance/quick_log_controller'
import RecallDrillModeController from 'controllers/compliance/recall_drill_mode_controller'
import ClipboardController from 'controllers/compliance/clipboard_controller'
import AllergenLineModeController from 'controllers/compliance/allergen_line_mode_controller'
import QuantityChipsController from 'controllers/production/quantity_chips_controller'
import HoldToConfirmController from 'controllers/production/hold_to_confirm_controller'
import TaskSearchController from 'controllers/production/task_search_controller'
import RowStepperController from 'controllers/production/row_stepper_controller'
import ProgramRunnerController from 'controllers/compliance/program_runner_controller'
import AssessmentRowsController from 'controllers/compliance/assessment_rows_controller'
import ChecklistBuilderController from 'controllers/compliance/checklist_builder_controller'
import FosNavController from 'controllers/fos_nav_controller'
import FosRevealController from 'controllers/fos_reveal_controller'
import FosPlansController from 'controllers/fos_plans_controller'
import { OnboardingChecklistController } from 'flaming_os/onboarding-checklist'
import CharacterCounter from '@stimulus-components/character-counter'
import { IngredientFormController } from 'flaming_os/ingredient-form'
import { PinPadController } from 'controllers/hr/pin_pad_controller'
import { KioskFilterController } from 'controllers/hr/kiosk_filter_controller'
import { KioskClockController } from 'controllers/hr/kiosk_clock_controller'
import { KioskRedirectController } from 'controllers/hr/kiosk_redirect_controller'

// Make Turbo available globally
window.Turbo = Turbo

// Initialize ActiveStorage
ActiveStorage.start()

// Initialize ActionCable
const consumer = createConsumer()
// UserSessionChannel is presence-only (closes the Ahoy visit on unsubscribe). Live DOM
// updates ride turbo-rails' own Turbo::StreamsChannel via turbo_stream_from (see above).
consumer.subscriptions.create('UserSessionChannel', {})

// Initialize Sentry
const sentryDsn = document.querySelector('meta[name="sentry-dsn"]')?.content

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: document.querySelector('meta[name="sentry-environment"]')?.content || 'production',
    tracesSampleRate: 0.1
  })
}

window.Sentry = Sentry

// Register Bali components (registers all Stimulus controllers from bali-view-components)
registerAll(application)

// Configure Stimulus development experience
application.debug = false
window.Stimulus = application

// Install global error handler for Stimulus controllers
application.handleError = (error, message, detail) => {
  console.error(message, detail)
  Sentry.captureException(error, { extra: { message, detail } })
}

// Configure ahoy analytics
ahoy.configure({
  visitsUrl: '/users/visits',
  visitParams: {
    screen_dimensions: `${window.screen.width}x${window.screen.height}`
  }
})

application.register('scroll-reveal', ScrollReveal)
application.register('native-bridge-message-sender', NativeBridgeMessageSenderController)
application.register('fixed-navbar-with-banner', FixedNavbarWithBannerController)
application.register('facebook-events', FacebookEventsController)
application.register('google-ads', GoogleAdsController)
application.register('selected-item', SelectedItemController)
application.register('delivery-time-field', DeliveryTimeFieldController)
application.register('feedback--form', FeedbackFormController)
application.register('slim-select-with-disabler', SlimSelectWithDisablerController)
application.register('stripe-checkout', StripeCheckoutController)
application.register('password-visibility', PasswordVisibilityController)
application.register('billing-toggle', BillingToggleController)
application.register('textarea-autogrow', TextareaAutogrow)
application.register('recipe-step-navigator', RecipeStepNavigatorController)
application.register('onboarding-checklist', OnboardingChecklistController)
application.register('character-counter', CharacterCounter)
application.register('ingredient-form', IngredientFormController)
application.register('homepage', HomepageController)
application.register('acct--invoice-form', InvoiceFormController)
application.register('compliance--signature-challenge', SignatureChallengeController)
application.register('compliance--decision-tree', DecisionTreeController)
application.register('compliance--critical-limit', CriticalLimitController)
application.register('compliance--ccp-keypad', CcpKeypadController)
application.register('compliance--corrective-action-form', CorrectiveActionFormController)
application.register('compliance--quick-log', QuickLogController)
application.register('compliance--recall-drill-mode', RecallDrillModeController)
application.register('compliance--clipboard', ClipboardController)
application.register('compliance--allergen-line-mode', AllergenLineModeController)
application.register('production--quantity-chips', QuantityChipsController)
application.register('production--task-search', TaskSearchController)
application.register('production--row-stepper', RowStepperController)
application.register('production--hold-to-confirm', HoldToConfirmController)
application.register('compliance--program-runner', ProgramRunnerController)
application.register('compliance--assessment-rows', AssessmentRowsController)
application.register('compliance--checklist-builder', ChecklistBuilderController)
application.register('fos-nav', FosNavController)
application.register('fos-reveal', FosRevealController)
application.register('fos-plans', FosPlansController)
application.register('hr--pin-pad', PinPadController)
application.register('hr--kiosk-filter', KioskFilterController)
application.register('hr--kiosk-clock', KioskClockController)
application.register('hr--kiosk-redirect', KioskRedirectController)

export { application }
